import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PrefsProvider } from "./context/PrefsContext";
import { BrandProvider } from "./context/BrandContext";
import InstallPrompt from "./components/InstallPrompt";
import "./App.css";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BookingPage from "./pages/BookingPage";
import TrainerLayout from "./pages/trainer/TrainerLayout";
import TrainerDashboard from "./pages/trainer/TrainerDashboard";
import TrainerSchedule from "./pages/trainer/TrainerSchedule";
import TrainerClients from "./pages/trainer/TrainerClients";
import TrainerClientProfile from "./pages/trainer/TrainerClientProfile";
import TrainerInvoices from "./pages/trainer/TrainerInvoices";
import TrainerNotifications from "./pages/trainer/TrainerNotifications";
import TrainerPayroll from "./pages/trainer/TrainerPayroll";
import TrainerBot from "./pages/trainer/TrainerBot";
import TrainerTeam from "./pages/trainer/TrainerTeam";
import TrainerEarnings from "./pages/trainer/TrainerEarnings";
import TrainerMetrics from "./pages/trainer/TrainerMetrics";
import TrainerAssessment from "./pages/trainer/TrainerAssessment";
import TrainerAnalytics from "./pages/trainer/TrainerAnalytics";
import TrainerPackages from "./pages/trainer/TrainerPackages";
import TrainerProgress from "./pages/trainer/TrainerProgress";
import TrainerCoaching from "./pages/trainer/TrainerCoaching";
import TrainerSettings from "./pages/trainer/TrainerSettings";
import ClientLayout from "./pages/client/ClientLayout";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientSessions from "./pages/client/ClientSessions";
import ClientInvoices from "./pages/client/ClientInvoices";
import ClientMetrics from "./pages/client/ClientMetrics";
import ClientAssessment from "./pages/client/ClientAssessment";
import ClientPackages from "./pages/client/ClientPackages";
import ClientProgress from "./pages/client/ClientProgress";
import ClientSettings from "./pages/client/ClientSettings";
import ClientAway from "./pages/client/ClientAway";
import ClientPaymentMethod from "./pages/client/ClientPaymentMethod";
import ClientWelcome from "./pages/client/ClientWelcome";
import ClientProfile from "./pages/client/ClientProfile";
import ClientHabits from "./pages/client/ClientHabits";
import ClientCheckin from "./pages/client/ClientCheckin";
import ClientForms from "./pages/client/ClientForms";
import ClientGroupClasses from "./pages/client/ClientGroupClasses";
import ClientDiscover from "./pages/client/ClientDiscover";
import PublicPackage from "./pages/PublicPackage";
import PublicLanding from "./pages/PublicLanding";
import PublicGroupSchedule from "./pages/PublicGroupSchedule";
import TrainerGroupSessions from "./pages/trainer/TrainerGroupSessions";
import PaymentSuccess from "./pages/client/PaymentSuccess";
import InstructorPortal from "./pages/instructor/InstructorPortal";
import MemberLayout from "./pages/member/MemberLayout";
import MemberClasses from "./pages/member/MemberClasses";
import MemberProfile from "./pages/member/MemberProfile";
import MemberReceipts from "./pages/member/MemberReceipts";
import { homeFor } from "./lib/home";

export { homeFor };

function Protected({ children, role, member }) {
  const { user, booting } = useAuth();
  if (booting || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="tracker">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={homeFor(user)} replace />;
  }
  if (role === "client" && (user.account_kind === "member") !== !!member) {
    return <Navigate to={homeFor(user)} replace />;
  }
  return children;
}

function RoleRedirect() {
  const { user, booting } = useAuth();
  if (booting) return null;
  if (!user) return <Landing />;
  return <Navigate to={homeFor(user)} replace />;
}

function App() {
  return (
    <PrefsProvider>
      <AuthProvider>
        <BrandProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<RoleRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/p/:slug" element={<PublicPackage />} />
            <Route path="/c/:slug" element={<PublicLanding />} />
            <Route path="/g/:slug" element={<PublicGroupSchedule />} />
            <Route path="/instructor" element={<Protected role="instructor"><InstructorPortal /></Protected>} />

            <Route
              path="/trainer"
              element={
                <Protected role="trainer">
                  <TrainerLayout />
                </Protected>
              }
            >
              <Route index element={<TrainerDashboard />} />
              <Route path="schedule" element={<TrainerSchedule />} />
              <Route path="group" element={<TrainerGroupSessions />} />
              <Route path="clients" element={<TrainerClients />} />
              <Route path="clients/:id" element={<TrainerClientProfile />} />
              <Route path="invoices" element={<TrainerInvoices />} />
              <Route path="notifications" element={<TrainerNotifications />} />
              <Route path="payroll" element={<TrainerPayroll />} />
              <Route path="bot" element={<TrainerBot />} />
              <Route path="team" element={<TrainerTeam />} />
              <Route path="earnings" element={<TrainerEarnings />} />
              <Route path="metrics" element={<TrainerMetrics />} />
              <Route path="assessment" element={<TrainerAssessment />} />
              <Route path="analytics" element={<TrainerAnalytics />} />
              <Route path="packages" element={<TrainerPackages />} />
              <Route path="progress" element={<TrainerProgress />} />
              <Route path="coaching" element={<TrainerCoaching />} />
              <Route path="settings" element={<TrainerSettings />} />
            </Route>

            <Route
              path="/client"
              element={
                <Protected role="client">
                  <ClientLayout />
                </Protected>
              }
            >
              <Route index element={<ClientDashboard />} />
              <Route path="sessions" element={<ClientSessions />} />
              <Route path="invoices" element={<ClientInvoices />} />
              <Route path="packages" element={<ClientPackages />} />
              <Route path="group" element={<ClientGroupClasses />} />
              <Route path="discover" element={<ClientDiscover />} />
              <Route path="metrics" element={<ClientMetrics />} />
              <Route path="assessment" element={<ClientAssessment />} />
              <Route path="progress" element={<ClientProgress />} />
              <Route path="habits" element={<ClientHabits />} />
              <Route path="checkin" element={<ClientCheckin />} />
              <Route path="forms" element={<ClientForms />} />
              <Route path="settings" element={<ClientSettings />} />
              <Route path="profile" element={<ClientProfile />} />
              <Route path="away" element={<ClientAway />} />
              <Route path="payment-method" element={<ClientPaymentMethod />} />
            </Route>

            <Route
              path="/client/welcome"
              element={
                <Protected role="client">
                  <ClientWelcome />
                </Protected>
              }
            />

            <Route
              path="/member"
              element={
                <Protected role="client" member>
                  <MemberLayout />
                </Protected>
              }
            >
              <Route index element={<MemberClasses />} />
              <Route path="discover" element={<ClientDiscover />} />
              <Route path="receipts" element={<MemberReceipts />} />
              <Route path="profile" element={<MemberProfile />} />
            </Route>

            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          </Routes>
        </BrowserRouter>
        </BrandProvider>
      </AuthProvider>
    </PrefsProvider>
  );
}

export default App;
